/**
 * CardActionPopup — opens when a player clicks one of their hand cards.
 *
 * Shows the full card detail (image + DB fields + rules text) plus the
 * play-option buttons appropriate for that card type and the current game
 * state. Buttons that aren't legal right now render greyed-out with a
 * "why disabled" tooltip — never hidden — so the player can still see
 * what would otherwise be available.
 *
 * Per the design rules:
 *
 *   Attack/Defend in hand:
 *     - ATK     (red)    : aggressor, has priority, sub-phase = aggressor-pre-attack
 *     - DEF     (blue)   : defender, has priority, sub-phase = defender-pre-block
 *     - Counter (yellow) : the card has counterGrants AND its counter
 *                          condition (counterScope, counterConditionType,
 *                          counterConditionValue) matches the live combat
 *                          slot. Anytime that condition is met (free
 *                          reaction window OR aggressor turn).
 *
 *   Utility in hand (single Play button — purple):
 *     - Maneuver : anytime player has priority AND card.utilityFields
 *                  conditions are met (we currently surface
 *                  canPlayUtilityFreeWindow as the engine gate).
 *     - Item     : aggressor with priority (sub-phase aggressor-pre-attack).
 *     - Reaction : whenever the freeReactionWindow is open AND the player
 *                  has priority (or is in their own attack window).
 *
 *   Stance in hand (single Play button — purple):
 *     - Aggressor with priority. When played, replaces any existing stance
 *       in the player's stance slot (engine handles the swap).
 */
import { X } from "lucide-react";
import type { Card, CounterRule } from "@/types/card";
import type { GameState, CombatSlot } from "@/types/game";
import { cn } from "@/lib/utils";
import { canPlayUtilityNow, previewEffectiveCost } from "@/game/playtestEngine";
import { getCardHooks } from "@/game/cards/registry";

const opp = (n: 1 | 2): 1 | 2 => (n === 1 ? 2 : 1);

/**
 * Where the popup was opened from. Engine APIs differ per source:
 *   hand     → instanceId is the handInstances id
 *   sig      → instanceId is the cardId; sigSlotIdx must be passed
 *   equip    → instanceId is the cardId; equipSlotIdx must be passed (Equipment only)
 *   armor    → no instance/slot; engine looks up player.armorCardId
 *
 * VIEW-ONLY sources (no action buttons today — reserved for future
 * "Activate Ability" wiring on persistent board cards):
 *   champion → the player's champion card
 *   stance   → the player's currently-equipped stance
 *   played   → a card sitting in the Played Cards zone (any non-A/D played
 *              this round — sigs, utilities, etc.)
 *   item     → a utility "Item" persisting in the Items zone
 */
export type PopupSource =
  | "hand"
  | "sig"
  | "equip"
  | "armor"
  | "champion"
  | "stance"
  | "played"
  | "item";

export interface CardActionPopupProps {
  /** The card the player clicked. */
  card: Card;
  /** Per-instance id (hand) or the card db id (sig/equip/armor — same as card.id). */
  instanceId: string;
  /** Which player owns this card. */
  playerNum: 1 | 2;
  /** Live game state, for computing button availability. */
  game: GameState;
  /** Card map for resolving the live attack card (counter condition checks + piercing). */
  cardMap: Map<string, Card>;
  /** Source of the popup (default "hand" for back-compat). */
  source?: PopupSource;
  /** Slot index for sig/equip sources. */
  slotIdx?: number;
  /** Close the popup. */
  onClose: () => void;
  /** Hand-card actions. */
  onPlayAttack?: (instanceId: string, cardDbId: string) => void;
  onPlayDefend?: (instanceId: string, cardDbId: string) => void;
  onActivateCounter?: (instanceId: string, cardDbId: string) => void;
  onPlayUtility?: (instanceId: string, cardDbId: string) => void;
  onPlayStance?: (instanceId: string, cardDbId: string) => void;
  onDiscardCard?: (instanceId: string, cardDbId: string) => void;
  /** Signature-move actions (slotIdx-based). */
  onPlaySigAttack?: (slotIdx: number) => void;
  onPlaySigDefend?: (slotIdx: number) => void;
  onPlaySigUtility?: (slotIdx: number) => void;
  /** Quick Learner copy actions — the sig slot houses a copy of a card whose
   *  cardType is NOT "Signature Move". The copy plays as its actual card type
   *  (A/D, Utility, Stance, Equipment) using these handlers. slotIdx-based. */
  onPlayQlSigAsAttack?: (slotIdx: number) => void;
  onPlayQlSigAsDefend?: (slotIdx: number) => void;
  onPlayQlSigAsUtility?: (slotIdx: number) => void;
  onPlayQlSigAsStance?: (slotIdx: number) => void;
  /** Equipment actions (slotIdx-based). */
  onTapEquipAttack?: (slotIdx: number) => void;
  onTapEquipDefend?: (slotIdx: number) => void;
  /** Armor block (no args). */
  onArmorBlock?: () => void;
  /** Bloodforged Plate — open the activation prompt to spend Blood counters. */
  onActivateBloodforged?: () => void;
  /**
   * Phase 3 — generic Item activation (e.g. Sharpening Mill "Activate" button).
   * Called with the item's instanceId and card db id. Engine dispatches to
   * the card's `onActivate` hook via `activateItemCard`.
   */
  onActivateItem?: (instanceId: string, cardId: string) => void;
}

// ─── Playability helpers ─────────────────────────────────────────────────────

interface ButtonState {
  enabled: boolean;
  reason?: string;
}

/** Most-recent unresolved combat slot (the active exchange). */
function findActiveSlot(slots: CombatSlot[]): CombatSlot | null {
  for (let i = slots.length - 1; i >= 0; i--) {
    if (!slots[i].resolved) return slots[i];
  }
  return null;
}

/** Whether this player is the aggressor in the *round* (priority concept). */
function isAggressor(game: GameState, n: 1 | 2): boolean {
  // The aggressor for the round is whoever has the active "playing" turn —
  // we use sub-phase + priority as the legal gate. A cleaner check: priority
  // matches AND we're in an aggressor-pre-attack subphase.
  return game.priority === n;
}

function evalAttack(game: GameState, n: 1 | 2, card?: Card): ButtonState {
  if (game.priority !== n) return { enabled: false, reason: "You don't have priority." };
  if (game.subPhase !== "aggressor-pre-attack")
    return { enabled: false, reason: "Not your attack window." };
  if (game.players[n].stunned) return { enabled: false, reason: "You're stunned." };
  // Per-card playability gate (e.g. Bloodied Edge needs 2 damaging attacks
  // this round before it can be played).
  if (card) {
    const hooks = getCardHooks(card);
    if (hooks?.canPlayAttack) {
      const gate = hooks.canPlayAttack({
        self: game.players[n],
        opponent: game.players[opp(n)],
        game,
        card,
      });
      if (!gate.enabled) {
        return { enabled: false, reason: gate.reason ?? "Cannot play right now." };
      }
    }
  }
  return { enabled: true };
}

function evalDefend(game: GameState, n: 1 | 2): ButtonState {
  if (game.priority !== n) return { enabled: false, reason: "You don't have priority." };
  if (game.subPhase !== "defender-pre-block")
    return { enabled: false, reason: "Not the defending window." };
  // Stunned: next attack OR defend chance is skipped — armor-block and
  // take-damage are still available. Stun is consumed when this defend
  // would have been played (via confirmPendingPlay), but we also gate the
  // UI here so the button reads disabled with a clear reason.
  if (game.players[n].stunned) return { enabled: false, reason: "You're stunned." };
  return { enabled: true };
}

/**
 * Counter eligibility (new structured schema).
 *
 * A card may have many `counterRules`. Each rule has a `trigger` (which
 * opposing card it answers) and `effects[]` (what it does). The button is
 * enabled when at least ONE rule's trigger matches the most recently played
 * opposing card on the active combat slot.
 *
 * Played-card vs opposing-card resolution:
 *   - Defender countering the aggressor's attack → opposing = slot.attackCard
 *   - Aggressor countering the defender's block  → opposing = slot.blockCard
 *   We pick whichever the player is NOT the owner of.
 */
function evalCounter(
  game: GameState,
  n: 1 | 2,
  card: Card,
  cardMap: Map<string, Card>,
  instanceId?: string,
): ButtonState {
  const rules = card.attackDefendFields?.counterRules ?? [];
  if (rules.length === 0) return { enabled: false, reason: "No counter on this card." };
  if (game.priority !== n) return { enabled: false, reason: "You don't have priority." };

  // Once this counter copy has been activated this round, it can't be
  // activated again — it's already on the field as a reference utility.
  if (instanceId && game.players[n].counterActivatedIds.has(instanceId)) {
    return { enabled: false, reason: "Counter already activated this round." };
  }

  // Quick lockdown: if the defender is responding to a Quick attack, counters
  // are not allowed — only defend / armor / take damage.
  if (
    game.subPhase === "defender-pre-block" &&
    incomingAttackIsQuick(game, n, cardMap)
  ) {
    return {
      enabled: false,
      reason: "Incoming Quick attack — only defend / armor / take damage allowed.",
    };
  }

  const slot = findActiveSlot(game.combatSlots ?? []);
  if (!slot) return { enabled: false, reason: "No active attack to counter." };

  // Determine the opposing-played card (the one that triggered this window).
  // - If we're the defender: the opposing play is the attacker's attack.
  // - If we're the attacker: the opposing play is the defender's block (if landed).
  const opposingZone =
    slot.attackerNum === n ? slot.blockCard : slot.attackCard;
  if (!opposingZone) return { enabled: false, reason: "No opposing card to counter." };
  const opposingCard = cardMap.get(opposingZone.cardId);
  if (!opposingCard) return { enabled: false, reason: "Opposing card unknown." };

  const opposingMode: "attack" | "defend" =
    slot.attackerNum === n ? "defend" : "attack";

  // Granted keywords from the slot (e.g. mechanics that added Stun mid-combat).
  const grantedKw =
    opposingMode === "attack"
      ? (slot.attackGrantedKeywords ?? [])
      : (slot.blockGrantedKeywords ?? []);

  // Live power/block of the opposing card on the slot — already includes any
  // power-bonus / block-bonus that fired when the card was played (baked into
  // adValueOverrides). Falls back to printed value when no override exists.
  const opOwnerN: 1 | 2 = slot.attackerNum === n ? (n === 1 ? 2 : 1) : slot.attackerNum;
  const opOwnerPlayer = game.players[opOwnerN];
  const opLiveValue = (mode: "attack" | "defend"): number => {
    const ovr = opOwnerPlayer.adValueOverrides.get(opposingZone.id);
    if (typeof ovr === "number") return ovr;
    if (mode === "attack") {
      return Number(
        opposingCard.attackDefendFields?.powerValue ??
          opposingCard.equipmentFields?.powerValue ??
          opposingCard.signatureMoveFields?.powerValue ??
          0,
      ) || 0;
    }
    return Number(
      opposingCard.attackDefendFields?.blockValue ??
        opposingCard.equipmentFields?.blockValue ??
        opposingCard.signatureMoveFields?.blockValue ??
        0,
    ) || 0;
  };

  for (const rule of rules) {
    if (counterRuleMatches(rule, opposingCard, opposingMode, grantedKw, opLiveValue)) {
      return { enabled: true };
    }
  }
  return { enabled: false, reason: "Counter trigger doesn't match the played card." };
}

/** Read weight from any opposing card form (A/D, equipment weapon by mode, sig). */
function opposingCardWeight(opCard: Card, opMode: "attack" | "defend"): string {
  const adWeight = opCard.attackDefendFields?.weight;
  if (adWeight) return adWeight.toLowerCase();
  const eqWeight =
    opMode === "attack"
      ? opCard.equipmentFields?.attackWeight
      : opCard.equipmentFields?.defendWeight;
  if (eqWeight) return eqWeight.toLowerCase();
  const sigWeight = opCard.signatureMoveFields?.attackWeight;
  if (sigWeight) return sigWeight.toLowerCase();
  return "";
}

/** True when a single CounterRule's trigger matches the opposing card. */
function counterRuleMatches(
  rule: CounterRule,
  opCard: Card,
  opMode: "attack" | "defend",
  opGrantedKeywords: string[],
  opLiveValue: (mode: "attack" | "defend") => number,
): boolean {
  const t = rule.trigger;

  // 1) actionPlayed gate.
  if (t.actionPlayed !== "either" && t.actionPlayed !== opMode) return false;

  // 2) weights gate (empty list = any). Reads weight from A/D, equipment weapon
  //    (per mode), or signature-move attack — whichever the opposing card uses.
  if (t.weights.length > 0) {
    const w = opposingCardWeight(opCard, opMode);
    const hit = t.weights.some((x) => x.toLowerCase() === w);
    if (!hit) return false;
  }

  // 3) attackTypes / defendTypes (matches whichever the opposing card was
  //    played as).
  if (opMode === "attack" && t.attackTypes.length > 0) {
    const at = (opCard.attackDefendFields?.attackType ?? "").toLowerCase();
    const hit = t.attackTypes.some((x) => x.toLowerCase() === at);
    if (!hit) return false;
  }
  if (opMode === "defend" && t.defendTypes.length > 0) {
    const dt = (opCard.attackDefendFields?.defendType ?? "").toLowerCase();
    const hit = t.defendTypes.some((x) => x.toLowerCase() === dt);
    if (!hit) return false;
  }

  // 4) hasKeywords (printed OR slot-granted).
  if (t.hasKeywords.length > 0) {
    const printed = (opCard.keywords ?? []).map((k) => k.toLowerCase());
    const granted = opGrantedKeywords.map((k) => k.toLowerCase());
    const all = new Set([...printed, ...granted]);
    const hit = t.hasKeywords.some((k) => all.has(k.toLowerCase()));
    if (!hit) return false;
  }

  // 5) live power/block gates (read post-buff values from adValueOverrides).
  if (typeof t.minBuffedPower === "number" && t.minBuffedPower > 0 && opMode === "attack") {
    if (opLiveValue("attack") < t.minBuffedPower) return false;
  }
  if (typeof t.minBuffedBlock === "number" && t.minBuffedBlock > 0 && opMode === "defend") {
    if (opLiveValue("defend") < t.minBuffedBlock) return false;
  }

  return true;
}

function evalUtility(
  game: GameState,
  n: 1 | 2,
  card: Card,
  cardMap: Map<string, Card>,
): ButtonState {
  // Quick lockdown takes precedence — defender vs Quick attack can't play
  // utilities (only defend / armor / take damage).
  if (
    game.subPhase === "defender-pre-block" &&
    incomingAttackIsQuick(game, n, cardMap)
  ) {
    return {
      enabled: false,
      reason: "Incoming Quick attack — only defend / armor / take damage allowed.",
    };
  }

  // Single source of truth: engine's canPlayUtilityNow. Handles priority,
  // stamina cost, subtype windows, and card-specific preconditions in one
  // place — so the Play button is correctly disabled for illegal plays.
  const result = canPlayUtilityNow(game, n, card);
  if (!result.ok) {
    return { enabled: false, reason: result.reason ?? "Cannot play right now." };
  }
  // Per-card playability gate (canPlayUtility hook) for utility cards that
  // need a card-text restriction beyond canPlayUtilityNow.
  const hooks = getCardHooks(card);
  if (hooks?.canPlayUtility) {
    const gate = hooks.canPlayUtility({
      self: game.players[n],
      opponent: game.players[opp(n)],
      game,
      card,
    });
    if (!gate.enabled) {
      return { enabled: false, reason: gate.reason ?? "Cannot play right now." };
    }
  }
  return { enabled: true };
}

function evalStance(game: GameState, n: 1 | 2): ButtonState {
  if (game.priority !== n) return { enabled: false, reason: "You don't have priority." };
  if (game.subPhase !== "aggressor-pre-attack")
    return { enabled: false, reason: "Stances play during your attack window." };
  if (!isAggressor(game, n)) return { enabled: false, reason: "Only the aggressor can swap stance." };
  return { enabled: true };
}

/** Most-recent unresolved attack against `n`. */
function findIncomingAttackOn(game: GameState, n: 1 | 2): CombatSlot | null {
  const slots = game.combatSlots ?? [];
  for (let i = slots.length - 1; i >= 0; i--) {
    const s = slots[i];
    if (!s.resolved && s.attackerNum !== n) return s;
  }
  return null;
}

/** True if the active pending attack on `n` has Piercing. */
function incomingAttackIsPiercing(
  game: GameState,
  n: 1 | 2,
  cardMap: Map<string, Card>,
): boolean {
  const slot = findIncomingAttackOn(game, n);
  if (!slot) return false;
  const c = cardMap.get(slot.attackCard.cardId);
  if (!c) return false;
  const has = (list: string[] | undefined) =>
    !!list?.some((k) => k.toLowerCase() === "piercing");
  return (
    has(c.attackKeywords) ||
    has(c.keywords) ||
    has((slot as { attackGrantedKeywords?: string[] }).attackGrantedKeywords)
  );
}

/** True if the active pending attack on `n` has Quick — defender may NOT play
 * counters or utility cards in the reaction window (only defend / armor /
 * take damage). */
function incomingAttackIsQuick(
  game: GameState,
  n: 1 | 2,
  cardMap: Map<string, Card>,
): boolean {
  const slot = findIncomingAttackOn(game, n);
  if (!slot) return false;
  const c = cardMap.get(slot.attackCard.cardId);
  if (!c) return false;
  const has = (list: string[] | undefined) =>
    !!list?.some((k) => k.toLowerCase() === "quick");
  return (
    has(c.attackKeywords) ||
    has(c.keywords) ||
    has((slot as { attackGrantedKeywords?: string[] }).attackGrantedKeywords)
  );
}

/** Sig move attack window — same gating as a hand attack. */
function evalSigAttack(game: GameState, n: 1 | 2, card?: Card): ButtonState {
  return evalAttack(game, n, card);
}

/** Sig move defend window — same gating as a hand defend. */
function evalSigDefend(game: GameState, n: 1 | 2): ButtonState {
  return evalDefend(game, n);
}

/** Sig move utility window — anytime aggressor priority OR a free reaction.
 *  Exception: a card with a `canPlayUtility` hook gets to override the
 *  default subPhase gate. Reversal, for example, is legal during the
 *  defender-pre-block window after 2 counters — its hook owns the timing
 *  rule. Cards without a hook keep the strict default. */
function evalSigUtility(game: GameState, n: 1 | 2, card?: Card): ButtonState {
  if (game.priority !== n) return { enabled: false, reason: "You don't have priority." };
  const hooks = card ? getCardHooks(card) : undefined;
  const hookOwnsTiming = !!hooks?.canPlayUtility;
  if (!hookOwnsTiming) {
    if (game.subPhase !== "aggressor-pre-attack" && !game.freeReactionWindow) {
      return { enabled: false, reason: "Only during your priority window." };
    }
  }
  // Per-card playability gate (e.g. Bloodied Edge needs 2 damaging attacks
  // this round before it can be played; Reversal needs 2 counters).
  if (card && hooks?.canPlayUtility) {
    const gate = hooks.canPlayUtility({
      self: game.players[n],
      opponent: game.players[opp(n)],
      game,
      card,
    });
    if (!gate.enabled) {
      return { enabled: false, reason: gate.reason ?? "Cannot play right now." };
    }
  }
  return { enabled: true };
}

/** Equipment attack — must be untapped, your attack window, not stunned. */
function evalEquipAttack(
  game: GameState,
  n: 1 | 2,
  slotIdx: number | undefined,
  card?: Card,
): ButtonState {
  if (slotIdx == null) return { enabled: false, reason: "Internal: missing slot." };
  const slot = game.players[n].equipSlots[slotIdx];
  if (!slot) return { enabled: false, reason: "Slot empty." };
  if (slot.tapped) return { enabled: false, reason: "Equipment already tapped this round." };
  if (slot.disarmed) return { enabled: false, reason: "Equipment is disarmed." };
  return evalAttack(game, n, card);
}

/** Equipment defend — must be untapped, defending window. */
function evalEquipDefend(
  game: GameState,
  n: 1 | 2,
  slotIdx: number | undefined,
): ButtonState {
  if (slotIdx == null) return { enabled: false, reason: "Internal: missing slot." };
  const slot = game.players[n].equipSlots[slotIdx];
  if (!slot) return { enabled: false, reason: "Slot empty." };
  if (slot.tapped) return { enabled: false, reason: "Equipment already tapped this round." };
  if (slot.disarmed) return { enabled: false, reason: "Equipment is disarmed." };
  return evalDefend(game, n);
}

/** Armor block — defender window, armor active, attack not Piercing. */
function evalArmorBlock(
  game: GameState,
  n: 1 | 2,
  cardMap: Map<string, Card>,
): ButtonState {
  if (game.priority !== n) return { enabled: false, reason: "You don't have priority." };
  if (game.subPhase !== "defender-pre-block")
    return { enabled: false, reason: "Not the defending window." };
  if (!game.players[n].armorActive)
    return { enabled: false, reason: "Armor is destroyed or inactive." };
  if (incomingAttackIsPiercing(game, n, cardMap))
    return { enabled: false, reason: "Incoming attack has Piercing — armor cannot block." };
  return { enabled: true };
}

// ─── Main popup ──────────────────────────────────────────────────────────────

export function CardActionPopup({
  card,
  instanceId,
  playerNum,
  game,
  cardMap,
  source = "hand",
  slotIdx,
  onClose,
  onPlayAttack,
  onPlayDefend,
  onActivateCounter,
  onPlayUtility,
  onPlayStance,
  onDiscardCard,
  onPlaySigAttack,
  onPlaySigDefend,
  onPlaySigUtility,
  onPlayQlSigAsAttack,
  onPlayQlSigAsDefend,
  onPlayQlSigAsUtility,
  onPlayQlSigAsStance,
  onTapEquipAttack,
  onTapEquipDefend,
  onArmorBlock,
  onActivateBloodforged,
  onActivateItem,
}: CardActionPopupProps) {
  const t = card.cardType;

  // Pull display fields.
  const ad = card.attackDefendFields;
  const sm = card.signatureMoveFields;
  const ut = card.utilityFields;
  const st = card.stanceFields;
  const eq = card.equipmentFields;
  const ar = card.armorFields;
  const power = ad?.powerValue || sm?.powerValue || eq?.powerValue || "";
  const block = ad?.blockValue || sm?.blockValue || eq?.blockValue || ar?.blockValue || "";
  const weight = ad?.weight || sm?.attackWeight || ar?.weight || "";
  const stamina = ad?.staminaCost || sm?.staminaCost || eq?.staminaCost || "";
  // Effective stamina preview — reflects stance / utility cost reductions
  // (Committed Form, Zeus' Wrathbrand) and any pending buffs that would
  // fire on this play (Tensed Core, Unbroken Sands, etc.).
  const playerForCost = game.players[playerNum];
  const baseCostNum = parseFloat(stamina || "0");
  const hasStaminaCost = !!stamina && !Number.isNaN(baseCostNum);
  const attackEffective = hasStaminaCost
    ? previewEffectiveCost(playerForCost, card, "attack", cardMap, game.round)
    : baseCostNum;
  const defendEffective = hasStaminaCost
    ? previewEffectiveCost(playerForCost, card, "defend", cardMap, game.round)
    : baseCostNum;
  // Show single number when both modes match, otherwise show min→max range.
  // For utility/stance/armor/champion etc, mode is null — both calls return
  // base cost so display is unchanged.
  const effLow = Math.min(attackEffective, defendEffective);
  const effHigh = Math.max(attackEffective, defendEffective);
  const effDiffersFromBase = hasStaminaCost && (effLow !== baseCostNum || effHigh !== baseCostNum);
  const atkType = ad?.attackType || sm?.attackType || "";
  const defType = ad?.defendType || "";
  const subtype = ut?.utilityType || "";
  const baseKw = card.keywords ?? [];
  const atkKw = card.attackKeywords ?? [];
  const defKw = card.defendKeywords ?? [];

  // Build button list per (source, card type).
  const buttons: { label: string; tone: "red" | "blue" | "yellow" | "purple" | "grey"; state: ButtonState; onClick?: () => void }[] = [];

  // Helper for sig-move/equip stat presence (string fields can be "" or "0").
  const hasStat = (v: string | undefined) => {
    if (!v) return false;
    const n = parseFloat(v);
    return !Number.isNaN(n) && n > 0;
  };

  if (source === "hand") {
    if (t === "Attack/Defend") {
      const atk = evalAttack(game, playerNum, card);
      const def = evalDefend(game, playerNum);
      const cnt = evalCounter(game, playerNum, card, cardMap, instanceId);
      buttons.push({
        label: "ATK",
        tone: "red",
        state: atk,
        onClick: onPlayAttack ? () => { onPlayAttack(instanceId, card.id); onClose(); } : undefined,
      });
      buttons.push({
        label: "DEF",
        tone: "blue",
        state: def,
        onClick: onPlayDefend ? () => { onPlayDefend(instanceId, card.id); onClose(); } : undefined,
      });
      if ((card.attackDefendFields?.counterRules ?? []).length > 0) {
        buttons.push({
          label: "Counter",
          tone: "yellow",
          state: cnt,
          onClick: onActivateCounter
            ? () => { onActivateCounter(instanceId, card.id); onClose(); }
            : undefined,
        });
      }
    } else if (t === "Utility") {
      const u = evalUtility(game, playerNum, card, cardMap);
      buttons.push({
        label: "Play",
        tone: "purple",
        state: u,
        onClick: onPlayUtility ? () => { onPlayUtility(instanceId, card.id); onClose(); } : undefined,
      });
    } else if (t === "Stance") {
      const s = evalStance(game, playerNum);
      buttons.push({
        label: "Play",
        tone: "purple",
        state: s,
        onClick: onPlayStance ? () => { onPlayStance(instanceId, card.id); onClose(); } : undefined,
      });
    }
    // Discard is the fallback secondary action for hand cards only.
    if (onDiscardCard) {
      buttons.push({
        label: "Discard",
        tone: "grey",
        state: { enabled: true },
        onClick: () => { onDiscardCard(instanceId, card.id); onClose(); },
      });
    }
  } else if (source === "sig") {
    // The sig slot may house either an actual Signature Move OR a Quick
    // Learner copy of some other card type (A/D, Utility, Stance, Equipment).
    // Branch on the live card's cardType so the action menu and the engine
    // dispatch path match the picked card's actual rules — not a generic
    // sig-move treatment.
    if (t === "Signature Move") {
      // Signature Move: ATK if powerValue, DEF if blockValue, both → both, neither → Play.
      const hasPwr = hasStat(sm?.powerValue);
      const hasBlk = hasStat(sm?.blockValue);
      if (hasPwr) {
        buttons.push({
          label: "ATK",
          tone: "red",
          state: evalSigAttack(game, playerNum, card),
          onClick:
            onPlaySigAttack && slotIdx != null
              ? () => { onPlaySigAttack(slotIdx); onClose(); }
              : undefined,
        });
      }
      if (hasBlk) {
        buttons.push({
          label: "DEF",
          tone: "blue",
          state: evalSigDefend(game, playerNum),
          onClick:
            onPlaySigDefend && slotIdx != null
              ? () => { onPlaySigDefend(slotIdx); onClose(); }
              : undefined,
        });
      }
      if (!hasPwr && !hasBlk) {
        buttons.push({
          label: "Play",
          tone: "purple",
          state: evalSigUtility(game, playerNum, card),
          onClick:
            onPlaySigUtility && slotIdx != null
              ? () => { onPlaySigUtility(slotIdx); onClose(); }
              : undefined,
        });
      }
    } else if (t === "Attack/Defend") {
      // Quick Learner copy of an A/D card — use the same legality gates as
      // a hand A/D play. The engine routes through playAttackDefend.
      buttons.push({
        label: "ATK",
        tone: "red",
        state: evalAttack(game, playerNum, card),
        onClick:
          onPlayQlSigAsAttack && slotIdx != null
            ? () => { onPlayQlSigAsAttack(slotIdx); onClose(); }
            : undefined,
      });
      buttons.push({
        label: "DEF",
        tone: "blue",
        state: evalDefend(game, playerNum),
        onClick:
          onPlayQlSigAsDefend && slotIdx != null
            ? () => { onPlayQlSigAsDefend(slotIdx); onClose(); }
            : undefined,
      });
    } else if (t === "Utility") {
      // Quick Learner copy of a Utility — single Play button using the same
      // legality as a hand utility play. Engine routes through playUtility.
      buttons.push({
        label: "Play",
        tone: "purple",
        state: evalUtility(game, playerNum, card, cardMap),
        onClick:
          onPlayQlSigAsUtility && slotIdx != null
            ? () => { onPlayQlSigAsUtility(slotIdx); onClose(); }
            : undefined,
      });
    } else if (t === "Stance") {
      // Quick Learner copy of a Stance — single Play button.
      buttons.push({
        label: "Play",
        tone: "purple",
        state: evalStance(game, playerNum),
        onClick:
          onPlayQlSigAsStance && slotIdx != null
            ? () => { onPlayQlSigAsStance(slotIdx); onClose(); }
            : undefined,
      });
    } else if (t === "Equipment") {
      // Quick Learner copy of an Equipment card — ATK/DEF mirror weapon stats.
      const eq = card.equipmentFields;
      const hasEqPwr = hasStat(eq?.powerValue);
      const hasEqBlk = hasStat(eq?.blockValue);
      if (hasEqPwr) {
        buttons.push({
          label: "ATK",
          tone: "red",
          state: evalAttack(game, playerNum, card),
          onClick:
            onPlayQlSigAsAttack && slotIdx != null
              ? () => { onPlayQlSigAsAttack(slotIdx); onClose(); }
              : undefined,
        });
      }
      if (hasEqBlk) {
        buttons.push({
          label: "DEF",
          tone: "blue",
          state: evalDefend(game, playerNum),
          onClick:
            onPlayQlSigAsDefend && slotIdx != null
              ? () => { onPlayQlSigAsDefend(slotIdx); onClose(); }
              : undefined,
        });
      }
    }
    // Champion / Armor / Token in a sig slot via Quick Learner: no buttons —
    // the engine refuses anyway. Player can re-pick a different card.
  } else if (source === "equip") {
    // Equipment (weapon): ATK if powerValue, DEF if blockValue.
    const eq = card.equipmentFields;
    const hasPwr = hasStat(eq?.powerValue);
    const hasBlk = hasStat(eq?.blockValue);
    if (hasPwr) {
      buttons.push({
        label: "ATK",
        tone: "red",
        state: evalEquipAttack(game, playerNum, slotIdx, card),
        onClick:
          onTapEquipAttack && slotIdx != null
            ? () => { onTapEquipAttack(slotIdx); onClose(); }
            : undefined,
      });
    }
    if (hasBlk) {
      buttons.push({
        label: "DEF",
        tone: "blue",
        state: evalEquipDefend(game, playerNum, slotIdx),
        onClick:
          onTapEquipDefend && slotIdx != null
            ? () => { onTapEquipDefend(slotIdx); onClose(); }
            : undefined,
      });
    }
  } else if (source === "armor") {
    buttons.push({
      label: "Block",
      tone: "blue",
      state: evalArmorBlock(game, playerNum, cardMap),
      onClick: onArmorBlock
        ? () => { onArmorBlock(); onClose(); }
        : undefined,
    });
    // Bloodforged Plate — "Activate Bloodforged" appears when this armor is
    // Bloodforged Plate and it has at least 1 Blood counter on it.
    if (
      card.name === "Bloodforged Plate"
      && (game.players[playerNum].armorBloodCounters ?? 0) > 0
    ) {
      buttons.push({
        label: "Activate Bloodforged",
        tone: "red",
        state: { enabled: true },
        onClick: onActivateBloodforged
          ? () => { onActivateBloodforged(); onClose(); }
          : undefined,
      });
    }
  } else if (
    source === "champion" ||
    source === "stance" ||
    source === "played"
  ) {
    // View-only sources — the popup renders the full card details (image,
    // stats, mechanics text) but no action buttons.
  } else if (source === "item") {
    // Phase 3 — in-play Item activation. If this card has an `onActivate`
    // hook in the registry, show an "Activate" button (once per round,
    // gated by onceFlagsThisRound).
    const itemHooks = getCardHooks(card);
    if (itemHooks?.onActivate) {
      const flagKey = `sharpeningMill-activate-${instanceId}`;
      const alreadyActivated = !!(game.players[playerNum].onceFlagsThisRound?.[flagKey]);
      buttons.push({
        label: "Activate",
        tone: "purple",
        state: alreadyActivated
          ? { enabled: false, reason: "Already activated this round" }
          : { enabled: true },
        onClick: onActivateItem && !alreadyActivated
          ? () => { onActivateItem(instanceId, card.id); onClose(); }
          : undefined,
      });
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/65 p-3 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-xl border-2 border-amber-soft/50 bg-[#0e1014] p-3 shadow-2xl shadow-black/70 sm:max-w-lg sm:p-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-2 top-2 rounded p-1 text-[#94a3b8] hover:bg-[#22262f] hover:text-white"
        >
          <X size={16} />
        </button>

        {/* Header */}
        <div className="mb-3 pr-7">
          <p className="text-[10px] uppercase tracking-widest text-[#64748b]">
            {t}
            {subtype ? ` · ${subtype}` : ""}
          </p>
          <h2 className="text-lg font-bold leading-tight text-white sm:text-xl">{card.name}</h2>
        </div>

        {/* Body: image + stats */}
        <div className="grid grid-cols-[7rem_1fr] gap-3 sm:grid-cols-[9rem_1fr]">
          <div className="relative h-40 w-28 overflow-hidden rounded-md border border-[#2a2f3a] bg-[#13151a] sm:h-52 sm:w-36">
            {card.imageDataUrl ? (
              <img
                src={card.imageDataUrl}
                alt={card.name}
                className="absolute inset-0 h-full w-full object-cover"
                draggable={false}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-5xl font-black text-white/15">
                {t?.[0] ?? "?"}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1.5 text-xs">
            {/* Stats row */}
            <div className="flex flex-wrap gap-1">
              {power && (
                <span className="rounded bg-red-500/30 px-1.5 py-0.5 font-mono text-[10px] font-bold text-red-200">
                  PWR {power}
                </span>
              )}
              {block && (
                <span className="rounded bg-blue-500/30 px-1.5 py-0.5 font-mono text-[10px] font-bold text-blue-200">
                  BLK {block}
                </span>
              )}
              {stamina && (
                effDiffersFromBase ? (
                  <span
                    className="rounded bg-emerald-500/30 px-1.5 py-0.5 font-mono text-[10px] font-bold text-emerald-200"
                    title="Effective cost after stance / buffs"
                  >
                    STAM <span className="text-emerald-200/60 line-through">{stamina}</span>{" "}
                    → {effLow === effHigh ? effLow : `${effLow}–${effHigh}`}
                  </span>
                ) : (
                  <span className="rounded bg-emerald-500/30 px-1.5 py-0.5 font-mono text-[10px] font-bold text-emerald-200">
                    STAM {stamina}
                  </span>
                )
              )}
              {weight && (
                <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-300">
                  {weight}
                </span>
              )}
            </div>
            {/* Type pills */}
            {(atkType || defType) && (
              <div className="flex flex-wrap gap-1">
                {atkType && (
                  <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-red-300">
                    Atk: {atkType}
                  </span>
                )}
                {defType && (
                  <span className="rounded bg-blue-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-blue-300">
                    Def: {defType}
                  </span>
                )}
              </div>
            )}
            {/* Keywords */}
            {(baseKw.length + atkKw.length + defKw.length) > 0 && (
              <div className="flex flex-wrap gap-1">
                {[...baseKw, ...atkKw, ...defKw].map((k, i) => (
                  <span
                    key={`${k}_${i}`}
                    className="rounded bg-violet-500/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-violet-300"
                  >
                    {k}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Rules text */}
        <div className="mt-3 space-y-1.5 rounded-md border border-[#2a2f3a] bg-[#13151a] p-2.5 text-[12px] leading-relaxed text-[#cbd5e1]">
          {ad?.attackAbility && (
            <p>
              <span className="font-semibold text-red-300">Attack: </span>
              {ad.attackAbility}
            </p>
          )}
          {ad?.defendAbility && (
            <p>
              <span className="font-semibold text-blue-300">Defend: </span>
              {ad.defendAbility}
            </p>
          )}
          {ad?.counterAbility && (
            <p>
              <span className="font-semibold text-amber-300">Counter: </span>
              {ad.counterAbility}
            </p>
          )}
          {sm?.ability && (
            <p>
              <span className="font-semibold text-violet-300">Signature: </span>
              {sm.ability}
            </p>
          )}
          {ut?.ability && (
            <p>
              <span className="font-semibold text-emerald-300">Utility: </span>
              {ut.ability}
            </p>
          )}
          {st?.ability && (
            <p>
              <span className="font-semibold text-teal-300">Stance: </span>
              {st.ability}
            </p>
          )}
          {eq?.ability && (
            <p>
              <span className="font-semibold text-amber-300">Equipment: </span>
              {eq.ability}
            </p>
          )}
          {ar?.ability && (
            <p>
              <span className="font-semibold text-cyan-300">Armor: </span>
              {ar.ability}
            </p>
          )}
          {card.rulesText && <p className="text-[#94a3b8]">{card.rulesText}</p>}
          {!ad?.attackAbility &&
            !ad?.defendAbility &&
            !ad?.counterAbility &&
            !sm?.ability &&
            !ut?.ability &&
            !st?.ability &&
            !eq?.ability &&
            !ar?.ability &&
            !card.rulesText && (
              <p className="italic text-[#64748b]">No rules text.</p>
            )}
        </div>

        {/* Buttons */}
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          {buttons.length === 0 ? (
            <span className="text-[10px] italic text-[#64748b]">
              View only · no actions available
            </span>
          ) : (
            buttons.map((b, i) => <ActionButton key={i} {...b} />)
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tone-aware button with disabled tooltip ─────────────────────────────────

const TONE_CLASS: Record<"red" | "blue" | "yellow" | "purple" | "grey", string> = {
  red: "bg-red-600/85 hover:bg-red-500 text-white border-red-400/60",
  blue: "bg-blue-600/85 hover:bg-blue-500 text-white border-blue-400/60",
  yellow: "bg-amber-500/85 hover:bg-amber-400 text-black border-amber-300/60",
  purple: "bg-violet-600/85 hover:bg-violet-500 text-white border-violet-400/60",
  grey: "bg-[#22262f] hover:bg-[#2a2f3a] text-[#cbd5e1] border-[#2a2f3a]",
};

function ActionButton({
  label,
  tone,
  state,
  onClick,
}: {
  label: string;
  tone: "red" | "blue" | "yellow" | "purple" | "grey";
  state: ButtonState;
  onClick?: () => void;
}) {
  const enabled = state.enabled && !!onClick;
  return (
    <button
      type="button"
      onClick={enabled ? onClick : undefined}
      disabled={!enabled}
      title={enabled ? undefined : state.reason}
      aria-disabled={!enabled}
      className={cn(
        "rounded-md border px-3.5 py-1.5 text-[11px] font-black uppercase tracking-wider shadow transition-colors",
        TONE_CLASS[tone],
        !enabled && "cursor-not-allowed opacity-40 grayscale",
      )}
    >
      {label}
    </button>
  );
}
